"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { useTranslations } from "@/lib/use-translations";

const CRITTERS = ["🐹", "🐿️", "🦔", "🐸", "🐰", "🦊"];
const GRID_SIZE = 9; // 3x3
// Adaptive speed: starts very relaxed, ramps up gently
const INITIAL_SHOW_TIME = 2500;
const MIN_SHOW_TIME = 700;
const INITIAL_SPAWN_INTERVAL = 1500;
const MIN_SPAWN_INTERVAL = 700;

function getShowTime(effectiveScore: number) {
  const t = Math.min(effectiveScore / 80, 1);
  const eased = 1 - Math.pow(1 - t, 2);
  return Math.round(INITIAL_SHOW_TIME - eased * (INITIAL_SHOW_TIME - MIN_SHOW_TIME));
}

function getSpawnInterval(effectiveScore: number) {
  const t = Math.min(effectiveScore / 80, 1);
  const eased = 1 - Math.pow(1 - t, 2);
  return Math.round(INITIAL_SPAWN_INTERVAL - eased * (INITIAL_SPAWN_INTERVAL - MIN_SPAWN_INTERVAL));
}

interface Hole {
  active: boolean;
  critter: string;
  whacked: boolean;
  id: number;
  spawnedAt: number; // timestamp for reaction time
}

export function WhackAMole() {
  const t = useTranslations();
  const g = t.games;

  const CHEERS = g.cheers;
  const SPEED_LEVELS = [
    { threshold: 0, label: "🐌", name: g.speedSlow },
    { threshold: 3, label: "🐢", name: g.speedSteady },
    { threshold: 6, label: "🐇", name: g.speedNormal },
    { threshold: 10, label: "🏃", name: g.speedQuick },
    { threshold: 15, label: "💨", name: g.speedFast },
    { threshold: 20, label: "⚡", name: g.speedBlazing },
    { threshold: 28, label: "🔥", name: g.speedOnFire },
    { threshold: 38, label: "🚀", name: g.speedRocket },
    { threshold: 50, label: "🌟", name: g.speedLegendary },
  ];

  const getSpeedLevel = (effectiveScore: number) => {
    let level = SPEED_LEVELS[0]!;
    for (const l of SPEED_LEVELS) {
      if (effectiveScore >= l.threshold) level = l;
    }
    return level;
  };

  const [holes, setHoles] = useState<Hole[]>(() =>
    Array.from({ length: GRID_SIZE }, () => ({
      active: false,
      critter: CRITTERS[0]!,
      whacked: false,
      id: 0,
      spawnedAt: 0,
    }))
  );
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [fastestWhack, setFastestWhack] = useState<number | null>(null);
  const [cheer, setCheer] = useState<{ text: string; key: number } | null>(null);
  const spawnIdRef = useRef(0);
  const scoreRef = useRef(0);
  const missesRef = useRef(0);
  const fastestRef = useRef<number | null>(null);
  const cheerKeyRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { missesRef.current = misses; }, [misses]);

  // Effective score for speed calculation — drops on misses
  const effectiveScore = Math.max(0, score - Math.floor(misses / 2));

  const spawnCritter = useCallback(() => {
    setHoles((prev) => {
      const inactive = prev
        .map((h, i) => ({ h, i }))
        .filter(({ h }) => !h.active);
      if (inactive.length === 0) return prev;

      const pick = inactive[Math.floor(Math.random() * inactive.length)]!;
      const critter = CRITTERS[Math.floor(Math.random() * CRITTERS.length)]!;
      const id = ++spawnIdRef.current;
      const spawnedAt = Date.now();

      const next = [...prev];
      next[pick.i] = { active: true, critter, whacked: false, id, spawnedAt };

      // Effective score for timeout calculation
      const eff = Math.max(0, scoreRef.current - Math.floor(missesRef.current / 2));
      const showTime = getShowTime(eff);
      const pickIndex = pick.i;
      setTimeout(() => {
        setHoles((current) => {
          const updated = [...current];
          const hole = updated[pickIndex];
          if (hole && hole.id === id && hole.active && !hole.whacked) {
            updated[pickIndex] = { ...hole, active: false, whacked: false };
            // Count as a miss
            setMisses((m) => m + 1);
          }
          return updated;
        });
      }, showTime);

      return next;
    });
  }, []);

  // Adaptive spawn loop
  useEffect(() => {
    spawnCritter();
    const interval = setInterval(spawnCritter, getSpawnInterval(effectiveScore));
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spawnCritter, getSpeedLevel(effectiveScore).threshold]);

  const whack = useCallback((index: number) => {
    setHoles((prev) => {
      const hole = prev[index];
      if (!hole || !hole.active || hole.whacked) return prev;
      const next = [...prev];
      next[index] = { ...hole, whacked: true };

      // Track reaction time
      const reactionMs = Date.now() - hole.spawnedAt;
      if (fastestRef.current === null || reactionMs < fastestRef.current) {
        fastestRef.current = reactionMs;
        setFastestWhack(reactionMs);
      }

      const snapshotId = hole.id;
      setTimeout(() => {
        setHoles((current) => {
          const updated = [...current];
          const cur = updated[index];
          if (cur && cur.id === snapshotId) {
            updated[index] = { ...cur, active: false, whacked: false };
          }
          return updated;
        });
      }, 200);
      return next;
    });
    setScore((s) => s + 1);
    const text = CHEERS[Math.floor(Math.random() * CHEERS.length)] ?? "";
    const key = ++cheerKeyRef.current;
    setCheer({ text, key });
    setTimeout(() => setCheer((c) => c?.key === key ? null : c), 800);
  }, []);

  const speedLevel = getSpeedLevel(effectiveScore);

  // Find any active critter to show in the instruction
  const activeCritter = holes.find((h) => h.active && !h.whacked);

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Instruction */}
      <div className="text-xs text-muted-foreground/60">
        {activeCritter
          ? <>{g.tapOn} {activeCritter.critter}</>
          : g.getReady}
      </div>

      {/* Score + Speed + Fastest */}
      <div className="flex items-center gap-3 text-sm flex-wrap justify-center">
        <span className="text-muted-foreground">
          {g.score}: <span className="text-foreground font-bold">{score}</span>
        </span>
        <span className="text-muted-foreground/50">|</span>
        <span className="text-muted-foreground">
          {speedLevel.label} {speedLevel.name}
        </span>
        {fastestWhack !== null && (
          <>
            <span className="text-muted-foreground/50">|</span>
            <span className="text-muted-foreground">
              {g.best}: <span className="text-foreground font-bold">{fastestWhack}ms</span>
            </span>
          </>
        )}
      </div>

      {/* Cheer float */}
      <div className="h-6 flex items-center justify-center">
        {cheer && (
          <span
            key={cheer.key}
            className="text-sm font-bold text-primary animate-cheer-float"
          >
            {cheer.text}
          </span>
        )}
      </div>

      {/* 3x3 Grid */}
      <div className="grid grid-cols-3 gap-2">
        {holes.map((hole, i) => (
          <button
            key={i}
            type="button"
            onClick={() => whack(i)}
            className={cn(
              "w-20 h-20 rounded-xl border-2 flex items-center justify-center transition-all duration-100 select-none",
              hole.active && !hole.whacked
                ? "border-primary/50 bg-primary/10 scale-100 cursor-pointer active:scale-90"
                : hole.whacked
                  ? "border-yellow-400 bg-yellow-400/20 scale-90"
                  : "border-border bg-muted/30 cursor-default"
            )}
            disabled={!hole.active || hole.whacked}
          >
            {hole.active && (
              <span
                className={cn(
                  "text-2xl transition-transform duration-100",
                  hole.whacked ? "scale-0 rotate-45" : "animate-bounce-in"
                )}
              >
                {hole.critter}
              </span>
            )}
            {!hole.active && (
              <div className="w-6 h-2 rounded-full bg-muted-foreground/20" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
